"use client";

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { cleanupCache } from "@/lib/cache/client-ai-cache";
import { reportClientError } from "@/lib/observability/report-client-error";

/**
 * Check if a query error indicates an expired/missing auth session.
 * Supabase RPC calls return specific error codes when not authenticated.
 */
function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as Record<string, unknown>;
  // Supabase PostgREST auth errors
  if (err.code === "PGRST301" || err.code === "42501") return true;
  // HTTP 401/403 from fetch-based queries
  if (err.status === 401 || err.status === 403) return true;
  // Supabase auth error messages
  const msg = String(err.message || "").toLowerCase();
  if (msg.includes("jwt expired") || msg.includes("not authenticated")) {
    return true;
  }
  return false;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    cleanupCache();
  }, []);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            // REGRESSION GUARD — NODE-1P9 (8 regressions, escalation level 3)
            // After K8s rollout 502 windows, users were stuck with stale null data
            // because queries never refetched after network recovery.
            // DO NOT: set this back to false.
            refetchOnReconnect: true,
            retry: (failureCount, error) => {
              // Don't retry auth errors — redirect to login instead
              if (isAuthError(error)) return false;
              return failureCount < 2;
            },
          },
          mutations: {
            onError: (error) => {
              if (isAuthError(error)) {
                window.location.href = "/login?error=session_expired";
                return;
              }
              // Safety net: report any mutation error not caught by the caller
              reportClientError(error, {
                source: "react-query.mutation.global",
                feature: "unknown",
                severity: "error",
              });
            },
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            // Redirect to login on auth errors
            if (isAuthError(error)) {
              console.error(
                "[QueryProvider] Auth error detected, redirecting to login:",
                error
              );
              window.location.href = "/login?error=session_expired";
              return;
            }
            // Report non-auth query errors
            reportClientError(error, {
              source: "react-query",
              severity: "warning",
              extra: {
                queryKey: JSON.stringify(query.queryKey).slice(0, 200),
              },
            });
          },
        }),
      })
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
